import { memo } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Wand2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const AutoTypeConfig = memo(({ data, onChange, inputColumns = [] }: any) => {

    const runDetection = () => {
        toast.info("Analyzing data sample for type inference...");
        setTimeout(() => toast.success("Type detection complete. 5 columns updated."), 1500);
    };

    const overrides = data.columnOverrides || [];

    const addOverride = () => {
        onChange('columnOverrides', [...overrides, { column: '', type: 'string', format: '' }]);
    };

    const updateOverride = (index: number, key: string, value: string) => {
        const newOverrides = [...overrides];
        newOverrides[index] = { ...newOverrides[index], [key]: value };
        onChange('columnOverrides', newOverrides);
    };

    const removeOverride = (index: number) => {
        const newOverrides = overrides.filter((_: any, i: number) => i !== index);
        onChange('columnOverrides', newOverrides);
    };

    const availableColumns = inputColumns.map((c: any) => c.name);

    return (
        <div className="space-y-6">
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800">
                <h4 className="font-semibold mb-1 flex items-center gap-1">
                    <Wand2 className="h-3 w-3" /> Auto Type
                </h4>
                <p>Automatically detect and convert column data types. Use manual overrides below to force specific formats.</p>
            </div>

            {/* Overrides Section */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Manual Overrides</Label>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addOverride}>
                        <Plus className="h-3 w-3 mr-1" /> Add 
                    </Button>
                </div>
                
                {overrides.length === 0 ? (
                    <div className="text-xs text-gray-500 italic p-3 border border-dashed rounded text-center">
                        No manual overrides. All columns will be auto-detected.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {overrides.map((override: any, idx: number) => (
                            <div key={idx} className="flex items-start gap-2 p-2 border rounded bg-gray-50/50">
                                <div className="flex-1 space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1 mt-0.5">
                                            {/* We use native select for simplicity since there are many columns often, or Select */}
                                            <select 
                                                className="w-full text-xs border rounded p-1.5 bg-white"
                                                value={override.column}
                                                onChange={(e) => updateOverride(idx, 'column', e.target.value)}
                                            >
                                                <option value="" disabled>Select Column...</option>
                                                {availableColumns.map((col: string) => (
                                                    <option key={col} value={col}>{col}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <Select
                                                value={override.type}
                                                onValueChange={(v) => updateOverride(idx, 'type', v)}
                                            >
                                                <SelectTrigger className="h-7 text-xs bg-white">
                                                    <SelectValue placeholder="Type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="string">String (Text)</SelectItem>
                                                    <SelectItem value="int">Integer</SelectItem>
                                                    <SelectItem value="float">Float (Decimal)</SelectItem>
                                                    <SelectItem value="date">Date / Datetime</SelectItem>
                                                    <SelectItem value="bool">Boolean</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    {override.type === 'date' && (
                                        <div className="flex items-center gap-2">
                                            <Label className="text-[10px] text-gray-500 whitespace-nowrap">Format String</Label>
                                            <Input 
                                                className="h-6 text-xs" 
                                                placeholder="e.g., %Y-%m-%d or %m/%d/%Y" 
                                                value={override.format || ''}
                                                onChange={(e) => updateOverride(idx, 'format', e.target.value)}
                                            />
                                        </div>
                                    )}
                                </div>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => removeOverride(idx)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Action */}
            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Invalid Values Fallback</Label>
                <div className="flex items-center gap-2">
                    <Select
                        value={data.fallbackValue || (data.coerceToNull !== false ? 'null' : 'error')}
                        onValueChange={(v) => onChange('fallbackValue', v)}
                    >
                        <SelectTrigger className="w-full text-xs">
                            <SelectValue placeholder="How to handle invalid values" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="null">Coerce invalid values to Null (Empty)</SelectItem>
                            <SelectItem value="zero">Coerce invalid values to Zero (0) / False</SelectItem>
                            <SelectItem value="error">Throw an Error</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">On Detection</Label>
                <RadioGroup
                    value={data.action || 'convert'}
                    onValueChange={(v) => onChange('action', v)}
                    className="flex flex-col space-y-1"
                >
                    <div className="flex items-center space-x-2">
                        <RadioGroupItem value="convert" id="act-convert" />
                        <Label htmlFor="act-convert" className="font-normal text-xs">Convert Types (Cast)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                        <RadioGroupItem value="flag" id="act-flag" />
                        <Label htmlFor="act-flag" className="font-normal text-xs">Flag Mismatches Only</Label>
                    </div>
                </RadioGroup>
            </div>

            <div className="pt-4 border-t">
                <Button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-xs"
                    onClick={runDetection}
                >
                    <Wand2 className="mr-2 h-3 w-3" /> Run Detection
                </Button>
            </div>
        </div>
    );
});
